import { Injectable } from '@angular/core';
import { PrintCardContentViewModel } from '../../shared/print-card-content-view-model';
import { combineLatest, Observable } from 'rxjs';
import { MasterKushStrainCardData } from '../../../../../../../models/print-cards/master-kush-strain-card-data';
import { map } from 'rxjs/operators';
import { SectionRowViewModelUtils } from '../../../../../../../utils/section-row-view-model-utils';
import { SortUtils } from '../../../../../../../utils/sort-utils';
import { PriceUtils } from '../../../../../../../utils/price-utils';

@Injectable()
export class MasterKushStrainIndexCardStackContentViewModel extends PrintCardContentViewModel {
  protected readonly MAX_STRAINS_THAT_CAN_APPEAR_ON_CARD = 4;

  public readonly cardData$: Observable<MasterKushStrainCardData>;

  public cardSizes$ = this.cardData$.pipe(
    map(cardData => cardData?.rowVariants?.map(v => cardData?.getGridQuantityAndSizeString(v)).unique())
  );

  public cardSize$ = this.cardSizes$.pipe(map(sizes => (sizes?.length > 1 ? 'ASSORTED SIZES' : sizes?.firstOrNull())));

  public variantsHaveAssortedSizes$ = this.cardSizes$.pipe(map(sizes => sizes?.length > 1));

  public hidePriceDecimal$ = this.cardData$.pipe(map(cardData => cardData?.menu?.menuOptions?.hidePriceDecimal));

  public prices$ = this.cardData$.pipe(
    map(cardData => {
      const theme = cardData?.menu?.theme;
      const locationId = cardData?.locationConfig?.locationId;
      const companyId = cardData?.companyConfig?.companyId;
      const priceStream = cardData?.locationConfig?.priceFormat;
      const hideSale = cardData?.menu?.menuOptions?.hideSale;
      return cardData?.rowVariants
        ?.map(v => v?.getVisiblePrice(theme, locationId, companyId, priceStream, hideSale))
        ?.unique(true)
        ?.sort(SortUtils.numberAscending);
    })
  );

  public minPrice$ = combineLatest([this.prices$, this.hidePriceDecimal$]).pipe(
    map(([prices, hidePriceDecimal]) => PriceUtils.formatPrice(prices?.firstOrNull(), hidePriceDecimal))
  );

  public maxPrice$ = combineLatest([this.prices$, this.hidePriceDecimal$]).pipe(
    map(([prices, hidePriceDecimal]) => PriceUtils.formatPrice(prices?.last(), hidePriceDecimal))
  );

  public rangedPrice$ = combineLatest([this.minPrice$, this.maxPrice$]).pipe(
    map(([minPrice, maxPrice]) => `${minPrice} - ${maxPrice}`)
  );

  public hasRange$ = this.prices$.pipe(map(prices => prices.length > 1));

  /**
   * Allows us to show the individual badges and label for each variant on the card.
   */
  public readonly visibleVariantVMs$ = this.cardData$.pipe(
    map(cardData => {
      const section = cardData?.section;
      const menu = cardData?.menu;
      const compConfig = cardData?.companyConfig;
      const locConfig = cardData?.locationConfig;
      const enabledProducts = [cardData?.product];
      const variants = cardData?.rowVariants?.take(this.MAX_STRAINS_THAT_CAN_APPEAR_ON_CARD);
      const singleVariantVM = SectionRowViewModelUtils.getRowVMsForListMode;
      return variants?.flatMap(v => singleVariantVM(section, menu, compConfig, locConfig, enabledProducts, [], [v.id]));
    })
  );

  public readonly nMoreStrains$ = this.cardData$.pipe(
    map(cardData => {
      const nMoreStrains = cardData?.rowVariants?.length - this.MAX_STRAINS_THAT_CAN_APPEAR_ON_CARD;
      return nMoreStrains > 0 ? nMoreStrains : 0;
    })
  );
}
